import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, finalize, first, Observable, Subject, takeUntil } from 'rxjs';
import { Messages } from 'src/app/common/enums';
import { CreateOrderRequest } from 'src/app/contracts/requests/create-order-request';
import { OrderService } from 'src/app/data/order.service';
import { PositionExpectationDto } from 'src/app/models/dtos/orders/position-expectation-dto';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';

@Component({
  selector: 'app-add-order-form',
  templateUrl: './add-order-form.component.html',
  styleUrls: ['./add-order-form.component.scss'],
  standalone: false
})
export class AddOrderFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private orderService = inject(OrderService);
  private spinner = inject(NgxSpinnerService);
  private snackbar = inject(SnackBarService);
  private router = inject(Router);

  orderForm: FormGroup;

  filteredPositionExpectations$: Observable<PositionExpectationDto[]>;
  maximumOrderItems = 5;

  private initialPositionExpectations: PositionExpectationDto[] = [];
  private positionExpectationsSubject = new BehaviorSubject<PositionExpectationDto[]>([]);
  private unsubscribe$ = new Subject<void>();

  constructor() { }

  ngOnInit(): void {
    this.orderForm = this.fb.group({
      orderItems: this.fb.array([])
    });

    this.orderService.getPositionExpectations().subscribe(data => {
      this.initialPositionExpectations = data;
      this.positionExpectationsSubject.next(data);
      this.filteredPositionExpectations$ = this.positionExpectationsSubject.asObservable();
      this.addItem();
      this.setupPositionExpectationChangeListener();
    });
  }

  get orderItems(): FormArray {
    return this.orderForm.get('orderItems') as FormArray;
  }

  createOrderItem(): FormGroup {
    let basePositionExpectation = null;
    const currentPositionExpectations = this.positionExpectationsSubject.getValue();
    if (currentPositionExpectations && currentPositionExpectations.length === 1) {
      basePositionExpectation = currentPositionExpectations[0];
    }

    return this.fb.group({
      employeesNumber: [null, [Validators.required, Validators.min(1), Validators.max(1000)]],
      positionExpectation: [basePositionExpectation, Validators.required],
      startDate: [null, Validators.required]
    });
  }

  addItem(): void {
    if (this.canAddOrderItems()) {
      if (this.orderItems.length === 1) {
        this.applyEmployerFilter();
      }

      const newItem = this.createOrderItem();
      this.orderItems.push(newItem);
    }
  }

  removeItem(index: number): void {
    if (this.canRemoveOrderItems()) {
      this.orderItems.removeAt(index);

      if (this.orderItems.length === 1) {
        this.positionExpectationsSubject.next(this.initialPositionExpectations);
      }
    }
  }

  canAddOrderItems(): boolean {
    return this.orderItems.length < this.maximumOrderItems;
  }

  canRemoveOrderItems(): boolean {
    return this.orderItems.length > 1;
  }

  submitOrder(): void {
    if (this.orderForm.invalid) {
      this.orderForm.markAllAsTouched();
      return;
    }

    const action$ = this.orderService.createOrder(this.request());
    this.spinner.show();
    action$
      .pipe(
        first(),
        finalize(() => this.spinner.hide()),
      )
      .subscribe((_) => {
        this.snackbar.openSuccessSnackBar(Messages.SuccessfullyCreatedOrder);
        this.router.navigate(['/orders']);
      });
  }

  private request(): CreateOrderRequest {
    const orderItems = this.orderItems?.value.map((item: any, index: number) => ({
      employeesNumber: item.employeesNumber,
      positionExpectationId: item.positionExpectation.Id,
      startDate: item.startDate,
      employerObjectId: item.positionExpectation.EmployerObjectId,
      itemNumber: index + 1,
    }));

    return {
      OrderItems: orderItems
    };
  }

  setupPositionExpectationChangeListener(): void {
    const firstPositionExpectation = this.getPositionExpectationControl(0);

    firstPositionExpectation?.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      if (this.orderItems.length > 1) {
        this.applyEmployerFilter();
      }
    });
  }

  applyEmployerFilter(): void {
    const firstPositionExpectation = this.getPositionExpectationControl(0);
    const selectedPositionExpectation = firstPositionExpectation?.value;

    if (selectedPositionExpectation && selectedPositionExpectation.EmployerId) {
      const currentValues = [];
      for (let i = 0; i < this.orderItems.length; i++) {
        const control = this.getPositionExpectationControl(i);
        if (control && control.value) {
          currentValues.push(control.value);
        }
      }

      const filteredList = this.initialPositionExpectations.filter((exp) => exp.EmployerId === selectedPositionExpectation.EmployerId);

      currentValues.forEach((value) => {
        if (value && value.Id) {
          const exists = filteredList.some((item) => item.Id === value.Id);
          if (!exists) {
            filteredList.push(value);
          }
        }
      });

      this.positionExpectationsSubject.next(filteredList);
    } else {
      this.positionExpectationsSubject.next(this.initialPositionExpectations);
    }
  }

  getPositionExpectationControl(index: number): FormGroup {
    return this.orderItems.at(index).get('positionExpectation') as FormGroup;
  }

  startDateFilter = (date: Date | null): boolean => {
    if (!date) {
      return false;
    }

    const selectedDate = moment(date);
    const today = moment().startOf('day');
    const momentEndDate = moment().add(1, 'year');

    return selectedDate.isSameOrAfter(today, 'day') &&
      selectedDate.isSameOrBefore(momentEndDate, 'day');
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}